import { createContext, useContext, useEffect, useState } from "react";

import { breakpoints, Breakpoint, pxToEm } from "@otta/design-tokens";

type Media = { tablet: boolean; desktop: boolean };

function addMediaListener(
  m: MediaQueryList,
  l: (e: { matches: boolean }) => void
): () => void {
  if (typeof m.addEventListener === "function") {
    m.addEventListener("change", l);

    return () => {
      m.removeEventListener("change", l);
    };
  }
  m.addListener(l);

  return () => {
    m.removeListener(l);
  };
}

const Context = createContext<Media | null>(null);

export function MediaProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const [media, setMedia] = useState<Media | null>(null);

  useEffect(() => {
    const tabletQuery = window.matchMedia(
      `(min-width: ${pxToEm(breakpoints.tablet)})`
    );

    const desktopQuery = window.matchMedia(
      `(min-width: ${pxToEm(breakpoints.tablet)})`
    );

    const init = {
      tablet: tabletQuery.matches,
      desktop: desktopQuery.matches,
    };

    setMedia(init);

    const unsubscribeTablet = addMediaListener(tabletQuery, e => {
      setMedia(m =>
        m ? { ...m, tablet: e.matches } : { ...init, tablet: e.matches }
      );
    });

    const unsubscribeDesktop = addMediaListener(desktopQuery, e => {
      setMedia(m =>
        m ? { ...m, desktop: e.matches } : { ...init, desktop: e.matches }
      );
    });

    return () => {
      unsubscribeTablet();
      unsubscribeDesktop();
    };
  }, []);

  return <Context.Provider value={media}>{children}</Context.Provider>;
}

export function useMedia(opts: { from: Breakpoint } | { to: Breakpoint }):
  | {
      loading: true;
    }
  | {
      loading: false;
      matches: boolean;
    } {
  const media = useContext(Context);

  if (media === null) {
    return { loading: true };
  }

  if ("to" in opts) {
    return {
      loading: false,
      matches:
        (opts.to == "tablet" && !media.tablet) ||
        (opts.to === "desktop" && !media.desktop),
    };
  }

  return {
    loading: false,
    matches:
      (opts.from === "desktop" && media.desktop) ||
      (opts.from === "tablet" && media.tablet),
  };
}
